/**
 * composerChip — a mention in the DRAFT BOX becomes the person, not the handle
 * (Miles, 2026-09-22: "when I go to mention someone in the text box draft box
 * like team, Malaika or Prest0n it doesn't turn into a special element ... to
 * inform the user is putting in the correct mention handle exactly", and
 * again: "a user mentions someone it should be the same styles showing the
 * full name").
 *
 * THE DANGER THIS MODULE IS SHAPED AROUND. The composer is an uncontrolled
 * contentEditable, and everything downstream of it reads TEXT:
 * `htmlToPlainText` walks the DOM and returns `textContent` with no hook for
 * an element, and the send path feeds that string to the mention grammar. So
 * the obvious chip — a span whose text is "Malaika Majeed" — transmits
 * "Malaika Majeed", `PLAIN_MENTION_SOURCE` matches nothing, and THE MENTION
 * SILENTLY STOPS NOTIFYING HER. A mention that looks confirmed and reaches
 * nobody is the exact failure the transcript chip exists to prevent.
 *
 * So the chip carries the handle IN ITS TEXT, at `font-size: 0`, AHEAD OF
 * EVERYTHING ELSE IN THE CHIP:
 *
 *   <span class="mention-chip mention-h-malaika">
 *     <span class="mention-chip-handle"> @malaika </span>  <- present, 0px
 *     <span class="mention-chip-glyph">M</span>
 *     <span class="mention-chip-name">Malaika Majeed</span>
 *   </span>
 *
 * The person sees the name; the DOM still says `@malaika`. That makes the
 * FAILURE MODE SAFE: a reader that knows about chips (`htmlToPlainText`, the
 * ONE door every composer reader already passes through — it consults
 * `isComposerChip` below) emits exactly `@malaika`, and a reader that does
 * not — `el.innerText` at GlobalChatSystem.tsx:4456, or a call site added in
 * a year by someone who never read this — emits ` @malaika MMalaika Majeed`,
 * which is cosmetically wrong and STILL NOTIFIES THE RIGHT PERSON. Every
 * design where the chip shows only the name has the opposite failure, and it
 * is silent.
 *
 * WHY THE HANDLE COMES FIRST, AND WHY BOTH SPACES ARE LOAD-BEARING. The
 * grammar is `(?:^|\s)@([A-Za-z0-9._%+-]+)`: the `@` must OPEN a word, and
 * `textContent` concatenates the chip's spans with nothing between them. Put
 * the glyph first and the naive reader sees `M@malaika` — the `@` is preceded
 * by a letter, the grammar matches NOTHING, and the safety net silently fails
 * in exactly the way this module exists to prevent (measured: the resolver
 * returned no uid at all). The leading space opens the word however the chip
 * is abutted; the trailing one keeps the name from running on as
 * `@malaikaMalaika`. Both are inside a `font-size: 0` span, so neither is
 * visible and neither moves the glyph off the name's left.
 *
 * WHY THE HANDLE IS ALSO IN A CLASS. `sanitizeHtmlInput` runs on EVERY input
 * event and keeps only `style` and `class` on a span — every `data-*` is
 * stripped, so a data attribute would not survive one keystroke. `class`
 * does survive typing, and the paste sanitizer removes every class from
 * pasted markup unconditionally, so a pasted span CANNOT forge a chip. That
 * asymmetry is what makes a class-encoded marker safe, and it is the reason
 * this needs no change to either sanitizer.
 *
 * Pure: strings and a DOM-free grammar. The composer glue lives in the host.
 */
import { PLAIN_MENTION_SOURCE } from './mentionResolver';
import { MENTION_CHIP_CLASS, MENTION_CHIP_GLYPH_CLASS } from './mentionChip';
import type { MentionChipPerson } from './mentionChip';

/** The span holding the handle at zero size — the send path's source of truth. */
export const MENTION_CHIP_HANDLE_CLASS = 'mention-chip-handle';
/** The span holding what the person reads. */
export const MENTION_CHIP_NAME_CLASS = 'mention-chip-name';
/** Prefix of the class that carries the handle, for a reader that has the DOM. */
export const MENTION_CHIP_HANDLE_PREFIX = 'mention-h-';

/**
 * What a chip weighs when the draft box is flattened to a string: exactly one
 * character, U+FFFC OBJECT REPLACEMENT CHARACTER — Unicode's own stand-in for
 * an embedded object inside text.
 *
 * It is neither whitespace nor a handle character, so a mention can never
 * open on it and a chip can never be re-chipped. See `FlatTextSegment` in
 * composerMentionPills.ts for why one character rather than zero.
 */
export const CHIP_PLACEHOLDER = '\uFFFC';

function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The chip's markup, for insertion into the composer.
 *
 * Correct AT THE PRODUCER: this goes through `execCommand('insertHTML')` and
 * then through `sanitizeHtmlInput`, which keeps spans and classes — so the
 * name and the glyph are escaped here and nothing downstream re-checks them.
 *
 * The handle span comes FIRST and is padded with a space on both sides. See
 * the module header: that ordering is what keeps the naive reader's text
 * resolvable, and it is pinned with a control that rebuilds the glyph-first
 * shape and proves the resolver reaches nobody.
 */
export function composerChipHtml(handle: string, person: MentionChipPerson): string {
  const safeHandle = String(handle ?? '').trim();
  const name = String(person?.name ?? '').trim();
  if (!safeHandle || !name) return '';
  const glyph = escapeHtml(person.glyph || name.charAt(0)).slice(0, 2);
  return `<span class="${MENTION_CHIP_CLASS} ${MENTION_CHIP_HANDLE_PREFIX}${escapeHtml(safeHandle)}">`
    + `<span class="${MENTION_CHIP_HANDLE_CLASS}"> @${escapeHtml(safeHandle)} </span>`
    + `<span class="${MENTION_CHIP_GLYPH_CLASS}">${glyph}</span>`
    + `<span class="${MENTION_CHIP_NAME_CLASS}">${escapeHtml(name)}</span>`
    + `</span>`;
}

/**
 * The draft's html with every chip written back as the handle the person
 * typed — what the SEND path must ship.
 *
 * A composer chip must never reach a sent message, and the reason is not
 * tidiness. `decorateMentionChips` runs over the finished html at RENDER time
 * and transforms TEXT, so the chip's own inner text would be decorated again:
 * a chip inside a chip, with the zero-sized handle span suddenly full size
 * because the bubble has no rule for it, reading "@malaika Malaika Majeed" to
 * everyone in the room. The stored message would also carry
 * `contenteditable="false"` in Firestore for the life of the thread.
 *
 * The transcript already has its own producer and its own resolver. What the
 * message needs to carry is what the person wrote — `@malaika` — and the
 * transcript makes its own chip out of it, every time it is rendered, against
 * whoever is in the thread THEN.
 *
 * Text in ⇒ text out, byte for byte, when there is no chip: the common case
 * costs one `indexOf`.
 */
export function stripComposerChips(html: string): string {
  const source = String(html ?? '');
  if (!source || source.indexOf(MENTION_CHIP_HANDLE_PREFIX) === -1) return source;
  if (typeof DOMParser === 'undefined') return source;
  try {
    const doc = new DOMParser().parseFromString(`<div id="r">${source}</div>`, 'text/html');
    const root = doc.getElementById('r');
    if (!root) return source;
    let touched = false;
    root.querySelectorAll('.mention-chip').forEach((el) => {
      if (!isComposerChip(el)) return;
      const handle = handleOfChipClass(el.className);
      if (!handle) return;
      el.replaceWith(doc.createTextNode(`@${handle}`));
      touched = true;
    });
    return touched ? root.innerHTML : source;
  } catch {
    // A draft that cannot be parsed still sends — as itself, which is what it
    // would have done before chips existed.
    return source;
  }
}

/** The handle a chip element carries, from its class. Null when it is not a chip. */
export function handleOfChipClass(className: string | null | undefined): string | null {
  for (const token of String(className ?? '').split(/\s+/)) {
    if (token.startsWith(MENTION_CHIP_HANDLE_PREFIX)) {
      const handle = token.slice(MENTION_CHIP_HANDLE_PREFIX.length);
      if (handle) return handle;
    }
  }
  return null;
}

/** True when this element is a composer mention chip. */
export function isComposerChip(el: Element | null | undefined): boolean {
  if (!el || el.nodeType !== 1) return false;
  return el.classList.contains(MENTION_CHIP_CLASS) && handleOfChipClass(el.className) !== null;
}

/**
 * Where a handle sits in a plain string, and what it resolved to — the input
 * to turning typed text into chips.
 *
 * A handle the caret is INSIDE is deliberately excluded. Miles chose "instant
 * mid-typing", and this is what that has to mean in an editor: the chip forms
 * the instant a handle resolves EXCEPT while it is the word being edited,
 * because replacing text under a live caret takes the caret with it — you
 * type `@mala`, the chip swallows it, and the `ika` lands after the chip. So
 * every resolved handle becomes a chip immediately, and the one under the
 * caret becomes one the moment the caret leaves it (a space, a click, the
 * next word). Picking from the dropdown is always instant, because the caret
 * is placed after the chip by the insert itself.
 */
export interface ComposerChipCandidate {
  start: number;
  end: number;
  handle: string;
  person: MentionChipPerson;
}

export function chipCandidatesIn(
  text: string,
  caret: number | null,
  resolve: (handle: string) => MentionChipPerson | null,
): ComposerChipCandidate[] {
  const source = String(text ?? '');
  if (!source || source.indexOf('@') === -1) return [];
  const mention = new RegExp(PLAIN_MENTION_SOURCE, 'g');
  const out: ComposerChipCandidate[] = [];
  let m: RegExpExecArray | null;
  while ((m = mention.exec(source)) !== null) {
    const captured = m[1] ?? '';
    const at = m.index + (m[0].length - captured.length - 1);
    const handle = captured.replace(/[._]+$/, '');
    if (!handle) continue;
    const end = at + handle.length + 1;
    mention.lastIndex = end;
    // The word being typed is left alone until the caret leaves it.
    if (caret !== null && caret > at && caret <= end) continue;
    const person = resolve(handle);
    if (!person || !String(person.name ?? '').trim()) continue;
    out.push({ start: at, end, handle, person });
  }
  return out;
}
